import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import { 
  Video, 
  Download, 
  Loader2, 
  Play, 
  Pause,
  CheckCircle2,
  AlertCircle,
  Settings,
  RotateCcw,
  Volume2,
  Maximize2
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function VideoGenerator({ 
  audioFile, 
  visuals, 
  lyrics
}) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [videoBlob, setVideoBlob] = useState(null);
  const [mp4Blob, setMp4Blob] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const ffmpegRef = useRef(null);
  const [settings, setSettings] = useState({
    resolution: "720p",
    fontStyle: "modern"
  });
  
  // Preview player state
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  // Filter to only images (videos not supported in slideshow yet)
  const imageVisuals = visuals.filter(v => v.type === 'image');
  const canGenerate = audioFile && imageVisuals.length > 0 && lyrics.trim();
  const lyricLines = lyrics ? lyrics.split('\n').filter(l => l.trim()) : [];

  // Cleanup URLs on unmount
  useEffect(() => {
    return () => {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [videoUrl, audioUrl]);

  const getFontSettings = (style) => {
    switch (style) {
      case 'classic':
        return { family: 'Georgia, serif', weight: 'normal', size: 48 };
      case 'bold':
        return { family: 'Arial Black, sans-serif', weight: 'bold', size: 52 };
      case 'minimal':
        return { family: 'Helvetica, Arial, sans-serif', weight: '300', size: 40 };
      default: // modern
        return { family: 'Inter, system-ui, sans-serif', weight: '600', size: 44 };
    }
  };

  const handleGenerate = async () => {
    if (!canGenerate) return;

    setIsGenerating(true);
    setProgress(0);
    setError(null);
    setVideoBlob(null);
    setVideoUrl(null);
    setAudioUrl(null);

    try {
      setProgressMessage("Preparing assets...");
      setProgress(5);

      // Create audio URL for playback
      const newAudioUrl = URL.createObjectURL(audioFile);
      
      // Load audio to get duration
      const tempAudio = new Audio(newAudioUrl);
      await new Promise((resolve, reject) => {
        tempAudio.onloadedmetadata = resolve;
        tempAudio.onerror = (e) => reject(new Error("Failed to load audio file"));
      });
      const audioDuration = tempAudio.duration;

      setProgressMessage("Loading images...");
      setProgress(15);

      // Load all visual images (use the pre-filtered imageVisuals from component scope)
      if (imageVisuals.length === 0) {
        throw new Error("At least one image is required. Videos are not yet supported.");
      }
      
      const loadedImages = await Promise.all(
        imageVisuals.map(async (v, index) => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              console.log(`Image ${index + 1} loaded: ${img.width}x${img.height}`);
              resolve(img);
            };
            img.onerror = (e) => {
              console.error(`Failed to load image ${index + 1}:`, v.previewUrl, e);
              reject(new Error(`Failed to load image ${index + 1}. Try re-uploading the image.`));
            };
            // Use previewUrl (from VisualsUploader)
            img.src = v.previewUrl;
          });
        })
      );
      
      console.log(`Loaded ${loadedImages.length} images successfully`);

      setProgressMessage("Setting up canvas...");
      setProgress(20);

      // Setup canvas
      const width = settings.resolution === '1080p' ? 1920 : 1280;
      const height = settings.resolution === '1080p' ? 1080 : 720;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      // Calculate timing - IMPORTANT: This must match exactly how the workstation calculates it
      const lineCount = lyricLines.length;
      const fps = 30;
      const totalFrames = Math.ceil(audioDuration * fps);
      const frameDuration = 1000 / fps; // ~33.33ms per frame at 30fps
      // Each lyric line gets equal time
      const timePerLine = lineCount > 0 ? audioDuration / lineCount : audioDuration;
      
      console.log(`Video timing: ${audioDuration}s audio, ${lineCount} lines, ${timePerLine.toFixed(2)}s per line, ${totalFrames} frames at ${fps}fps`);

      setProgressMessage("Rendering video...");
      setProgress(25);

      // Setup MediaRecorder - capture at 0 fps (manual frame push)
      const stream = canvas.captureStream(0);
      const videoTrack = stream.getVideoTracks()[0];
      
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8') 
        ? 'video/webm;codecs=vp8' 
        : 'video/webm';
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: settings.resolution === '1080p' ? 5000000 : 3000000
      });

      const chunks = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      const recordingPromise = new Promise((resolve) => {
        mediaRecorder.onstop = () => {
          const blob = new Blob(chunks, { type: 'video/webm' });
          resolve(blob);
        };
      });

      const fontSettings = getFontSettings(settings.fontStyle);

      // Start recording
      mediaRecorder.start(100);
      
      // Render each frame with proper timing
      // We need to render in real-time for MediaRecorder to capture correctly
      const renderStartTime = performance.now();
      
      for (let frame = 0; frame < totalFrames; frame++) {
        const currentAudioTime = frame / fps;
        
        // Calculate which lyric line should be shown at this time
        const currentLineIndex = lineCount > 0 
          ? Math.min(Math.floor(currentAudioTime / timePerLine), lineCount - 1)
          : -1;
        
        // Cycle through images evenly across the video duration
        const currentImageIndex = Math.floor(
          (currentAudioTime / audioDuration) * loadedImages.length
        ) % loadedImages.length;

        // Draw background image (cover fill)
        const img = loadedImages[Math.max(0, currentImageIndex)];
        const scale = Math.max(width / img.width, height / img.height);
        const x = (width - img.width * scale) / 2;
        const y = (height - img.height * scale) / 2;
        
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);

        // Add dark overlay for text readability
        ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
        ctx.fillRect(0, 0, width, height);

        // Draw current lyric line
        if (lineCount > 0 && currentLineIndex >= 0) {
          const line = lyricLines[currentLineIndex];
          ctx.font = `${fontSettings.weight} ${fontSettings.size}px ${fontSettings.family}`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          // Text shadow
          ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
          ctx.fillText(line, width / 2 + 3, height / 2 + 3);
          
          // Main text
          ctx.fillStyle = '#ffffff';
          ctx.fillText(line, width / 2, height / 2);
        }

        // Request a frame capture from the stream
        if (videoTrack.requestFrame) {
          videoTrack.requestFrame();
        }

        // Update progress
        if (frame % 30 === 0) {
          const renderProgress = 25 + (frame / totalFrames) * 65;
          setProgress(Math.min(90, Math.round(renderProgress)));
          setProgressMessage(`Rendering... ${Math.round((frame / totalFrames) * 100)}%`);
        }

        // Wait for the exact frame duration to keep timing correct
        const expectedTime = renderStartTime + (frame + 1) * frameDuration;
        const currentTime = performance.now();
        const waitTime = Math.max(1, expectedTime - currentTime);
        await new Promise(r => setTimeout(r, waitTime));
      }

      mediaRecorder.stop();

      setProgressMessage("Finalizing video frames...");
      setProgress(90);

      const webmBlob = await recordingPromise;
      
      // Now convert to MP4 with audio using FFmpeg
      setProgressMessage("Converting to MP4 with audio...");
      setProgress(92);
      
      try {
        const mp4Result = await convertToMp4WithAudio(webmBlob, audioFile);
        setMp4Blob(mp4Result);
        setVideoBlob(webmBlob); // Keep webm for preview
        setVideoUrl(URL.createObjectURL(mp4Result));
        setAudioUrl(newAudioUrl);
        setProgress(100);
        setProgressMessage("Complete!");
      } catch (conversionError) {
        console.error('MP4 conversion failed:', conversionError);
        // Fallback to WebM if conversion fails
        setVideoBlob(webmBlob);
        setVideoUrl(URL.createObjectURL(webmBlob));
        setAudioUrl(newAudioUrl);
        setProgress(100);
        setProgressMessage("Complete (WebM - MP4 conversion failed)");
      }

    } catch (err) {
      console.error('Video generation error:', err);
      setError(err.message || "Failed to generate video. Try a different browser or smaller files.");
    } finally {
      setIsGenerating(false);
    }
  };

  const convertToMp4WithAudio = async (webmBlob, audioFile) => {
    setIsConverting(true);
    
    try {
      // Initialize FFmpeg
      if (!ffmpegRef.current) {
        const ffmpeg = new FFmpeg();
        const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
        await ffmpeg.load({
          coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
          wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
        });
        ffmpegRef.current = ffmpeg;
      }
      
      const ffmpeg = ffmpegRef.current;
      
      // Write input files
      await ffmpeg.writeFile('video.webm', await fetchFile(webmBlob));
      await ffmpeg.writeFile('audio.mp3', await fetchFile(audioFile));
      
      // Convert to MP4 with audio
      await ffmpeg.exec([
        '-i', 'video.webm',
        '-i', 'audio.mp3',
        '-c:v', 'libx264',
        '-preset', 'fast',
        '-crf', '23',
        '-c:a', 'aac',
        '-b:a', '192k',
        '-shortest',
        '-movflags', '+faststart',
        'output.mp4'
      ]);
      
      // Read output
      const data = await ffmpeg.readFile('output.mp4');
      const mp4Blob = new Blob([data.buffer], { type: 'video/mp4' });
      
      // Cleanup
      await ffmpeg.deleteFile('video.webm');
      await ffmpeg.deleteFile('audio.mp3');
      await ffmpeg.deleteFile('output.mp4');
      
      return mp4Blob;
    } finally {
      setIsConverting(false);
    }
  };

  const handleDownload = () => {
    const blobToDownload = mp4Blob || videoBlob;
    if (!blobToDownload) return;
    
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blobToDownload);
    a.download = `lyric-video-${Date.now()}.${mp4Blob ? 'mp4' : 'webm'}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const togglePlayPause = () => {
    if (videoRef.current && audioRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        audioRef.current.pause();
      } else {
        // Sync audio to video position
        audioRef.current.currentTime = videoRef.current.currentTime;
        videoRef.current.play();
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (value) => {
    if (videoRef.current && audioRef.current) {
      videoRef.current.currentTime = value[0];
      audioRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  const handleVolumeChange = (value) => {
    if (audioRef.current) {
      audioRef.current.volume = value[0];
      setVolume(value[0]);
    }
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleReset = () => {
    setVideoBlob(null);
    setMp4Blob(null);
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setVideoUrl(null);
    setAudioUrl(null);
    setProgress(0);
    setError(null);
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    }
  };

  // Keep video and audio in sync
  const handleVideoTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
    setCurrentTime(0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#1a1a1a] border border-[#2a2a2a] mb-4">
          <Video className="w-8 h-8 text-cyan-400" />
        </div>
        <h2 className="text-2xl font-bold text-[#e0e0e0] mb-2">Generate Video</h2>
        <p className="text-[#808080]">
          Combine your audio, visuals, and lyrics into a lyric video
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-400 mb-1">
            {audioFile ? "✓" : "—"}
          </div>
          <div className="text-xs text-[#808080]">Audio</div>
        </div>
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-cyan-400 mb-1">
            {imageVisuals.length}
          </div>
          <div className="text-xs text-[#808080]">Images</div>
          {visuals.length > imageVisuals.length && (
            <div className="text-xs text-amber-400 mt-1">
              ({visuals.length - imageVisuals.length} video{visuals.length - imageVisuals.length !== 1 ? 's' : ''} ignored)
            </div>
          )}
        </div>
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-pink-400 mb-1">
            {lyricLines.length}
          </div>
          <div className="text-xs text-[#808080]">Lyric Lines</div>
        </div>
      </div>

      {/* Settings */}
      {!videoUrl && (
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-4 h-4 text-[#808080]" />
            <span className="text-sm font-medium text-[#a0a0a0]">Video Settings</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-[#606060] mb-1 block">Resolution</label>
              <Select 
                value={settings.resolution} 
                onValueChange={(v) => setSettings(s => ({ ...s, resolution: v }))}
              >
                <SelectTrigger className="bg-[#0a0a0a] border-[#2a2a2a]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="720p">720p (HD)</SelectItem>
                  <SelectItem value="1080p">1080p (Full HD)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#606060] mb-1 block">Font Style</label>
              <Select 
                value={settings.fontStyle} 
                onValueChange={(v) => setSettings(s => ({ ...s, fontStyle: v }))}
              >
                <SelectTrigger className="bg-[#0a0a0a] border-[#2a2a2a]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="modern">Modern</SelectItem>
                  <SelectItem value="classic">Classic</SelectItem>
                  <SelectItem value="bold">Bold</SelectItem>
                  <SelectItem value="minimal">Minimal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Validation Errors */}
      {!canGenerate && !videoUrl && (
        <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-200">
              <p className="font-medium mb-2">Missing requirements:</p>
              <ul className="text-amber-300/80 space-y-1">
                {!audioFile && <li>• Upload an audio file</li>}
                {imageVisuals.length === 0 && <li>• Add at least one image (videos not yet supported)</li>}
                {!lyrics.trim() && <li>• Enter your lyrics</li>}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Generation Error */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-900/20 border border-red-500/30 rounded-lg text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Progress */}
      {isGenerating && (
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
            <span className="text-[#e0e0e0]">{progressMessage}</span>
          </div>
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-[#606060] mt-2 text-right">{progress}%</p>
        </div>
      )}

      {/* Video Preview */}
      {videoUrl && !isGenerating && (
        <div className="bg-gradient-to-r from-green-900/20 to-cyan-900/20 border border-green-500/30 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-400" />
              <span className="text-lg font-medium text-[#e0e0e0]">
                Video Ready!
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              className="text-[#808080] hover:text-white"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              New Video
            </Button>
          </div>

          {/* Video Player with synced audio */}
          <div className="relative rounded-lg overflow-hidden bg-black">
            <video
              ref={videoRef}
              src={videoUrl}
              className="w-full aspect-video"
              onTimeUpdate={handleVideoTimeUpdate}
              onLoadedMetadata={() => setDuration(videoRef.current?.duration || 0)}
              onEnded={handleVideoEnded}
              muted
            />
            {/* Hidden audio element for synced playback */}
            <audio ref={audioRef} src={audioUrl} />
            
            {/* Fullscreen button overlay */}
            <button
              onClick={handleFullscreen}
              className="absolute top-3 right-3 p-2 bg-black/50 rounded-lg hover:bg-black/70 transition-colors"
            >
              <Maximize2 className="w-4 h-4 text-white" />
            </button>
          </div>

          {/* Video Controls */}
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <button
                onClick={togglePlayPause}
                className="p-3 rounded-full bg-purple-600 hover:bg-purple-700 transition-colors text-white"
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </button>
              
              <div className="flex-1">
                <Slider
                  value={[currentTime]}
                  max={duration || 100}
                  step={0.1}
                  onValueChange={handleSeek}
                  className="cursor-pointer"
                />
                <div className="flex justify-between text-xs text-[#606060] mt-1">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2 w-28">
                <Volume2 className="w-4 h-4 text-[#606060]" />
                <Slider
                  value={[volume]}
                  max={1}
                  step={0.01}
                  onValueChange={handleVolumeChange}
                  className="cursor-pointer"
                />
              </div>
            </div>
          </div>

          {/* Download Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleDownload}
              disabled={isConverting}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              {isConverting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Converting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download Video {mp4Blob ? '(.mp4)' : '(.webm)'}
                </>
              )}
            </Button>
          </div>

          {mp4Blob ? (
            <p className="text-xs text-center text-green-400">
              ✓ MP4 with audio included - ready to share!
            </p>
          ) : (
            <p className="text-xs text-center text-amber-400">
              WebM fallback - audio not embedded. Use a video editor to merge.
            </p>
          )}
        </div>
      )}

      {/* Generate Button */}
      {!videoUrl && !isGenerating && (
        <Button
          onClick={handleGenerate}
          disabled={!canGenerate}
          className="w-full h-14 text-lg bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white disabled:opacity-50"
        >
          <Video className="w-5 h-5 mr-2" />
          Generate Lyric Video
        </Button>
      )}

      {/* Info */}
      <div className="text-center text-xs text-[#606060]">
        <p>Video generation happens in your browser using Canvas API</p>
        <p className="mt-1">Works best in Chrome/Edge. This is completely free.</p>
      </div>
    </div>
  );
}
